import { useEffect, useState, useContext } from "react";
import { useParams, Link } from "react-router-dom";
import { Button, Modal } from "react-bootstrap";
import { FaPlus, FaSave, FaEdit, FaTrash } from "react-icons/fa";
import { request } from "../../../utils/api";
import { toast, ToastContainer } from "react-toastify";
import DataTable from "react-data-table-component";
import Loading from "../utilComponent/Loading";
import * as Yup from "yup";
import { AuthContext } from "../../../auth/AuthContext";
import { AbilityContext } from "../../../context/AbilityContext";

const validationSchema = Yup.object().shape({
  payment_date: Yup.date().required("Date is required"),
  deposit: Yup.number()
    .min(0, "Deposit cannot be negative")
    .required("Deposit is required"),
  payment: Yup.number()
    .min(0, "Payment cannot be negative")
    .required("Payment is required"),
  detail: Yup.string().required("Detail is required"),
  recurring_type: Yup.string()
    .oneOf(["none", "weekly", "monthly"])
    .required("Recurring option is required"),
});

const LeasureList = () => {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const canManage = ["super_admin", "admin"].includes(user?.role_key);
  const ability = useContext(AbilityContext);

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [errors, setErrors] = useState({});
  const [totalRecords, setTotalRecords] = useState({}) ;
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
  });
  const [formData, setFormData] = useState({
    payment_date: "",
    deposit: "",
    payment: "",
    detail: "",
    recurring_type: "none",
  });
  const getTableData = async () => {
    try {
      setLoading(true);
      let url = '';
      if (user?.role_key === "super_admin") {
        url = `loans?page=${pagination.current_page}&per_page=${pagination.per_page}&account_id=${user.account.id}&sheet_id=${id}`;
      }else{
        url = `loans?page=${pagination.current_page}&per_page=${pagination.per_page}&sheet_id=${id}`;
      }
      const res = await request({
        url: url,
        method: "GET",
      });

      if (res?.status) {
        setRecords(res.data.items || []);
        setTotalRecords(res.data.totals || []);
         setPagination({
          current_page: res.data.pagination.current_page,
          per_page: res.data.pagination.per_page,
          total: res.data.pagination.total,
          last_page: res.data.pagination.last_page,
        });
      }
    } catch {
      toast.error("Failed to load records");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTableData();
  }, []);


  const validateForm = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      const newErrors = {};
      err.inner.forEach((e) => (newErrors[e.path] = e.message));
      setErrors(newErrors);
      return false;
    }
  };

  const openAddModal = () => {
    setEditMode(false);
    setFormData({
      payment_date: "",
      deposit: "",
      payment: "",
      detail: "",
      recurring_type: "none",
    });
    setErrors({});
    setShowModal(true);
  };

  const openEditModal = (row) => {
    setEditMode(true);
    setSelectedRow(row);
    setFormData({
      payment_date: row.payment_date,
      deposit: row.deposit,
      payment: row.payment,
      detail: row.detail,
      recurring_type: row.recurring_type,
    });
    setErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRow(null);
  };

 
  const handleSave = async () => {
    const isValid = await validateForm();
    if (!isValid) return;

    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("payment_date", formData.payment_date);
      fd.append("deposit", formData.deposit);
      fd.append("payment", formData.payment);
      fd.append("detail", formData.detail);
      fd.append("recurring_type", formData.recurring_type);
      fd.append("sheet_id", id);

      if (user?.role === "super_admin") {
        fd.append("account_id", user.account.id);
      }

      if (editMode) {
        await request({
          url: `loans/${selectedRow.id}`,
          method: "POST",
          data: fd,
        });
        toast.success("Record updated");
      } else {
        await request({
          url: "loans",
          method: "POST",
          data: fd,
        });
        toast.success("Record added");
      }

      closeModal();
      getTableData();
    } catch {
      toast.error("Failed to save record");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm("Delete this record?")) return;

    try {
      setLoading(true);
      await request({
        url: `loans/${row.id}`,
        method: "DELETE",
      });
      toast.success("Record deleted");
      getTableData();
    } catch {
      toast.error("Failed to delete");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { name: "Date", selector: (row) => row.payment_date },
    { name: "Deposit", selector: (row) => row.deposit },
    { name: "Payment", selector: (row) => row.payment },
    { name: "Detail", selector: (row) => row.detail },
    { name: "Recurring", selector: (row) => row.recurring_type },
    canManage && {
      name: "Action",
      cell: (row) => (
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => openEditModal(row)}
          >
            <FaEdit />
          </button>
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => handleDelete(row)}
          >
            <FaTrash />
          </button>
        </div>
      ),
      width: "130px",
    },
  ].filter(Boolean);

  return (
    <>
    <ToastContainer/>
      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
        <ol className="breadcrumb bg-transparent mb-0">
          <li className="breadcrumb-item">
            <Link to="/">Home</Link>
          </li>
          <li className="breadcrumb-item">Calculation</li>
          <li className="breadcrumb-item active">Leasure List</li>
        </ol>
      </div>

      <div className="page-body px-4 py-3">
        <div className="card">
          <div className="card-header d-flex justify-content-between">
            <h6>Leasure List</h6>
            {canManage && (
              ability.can('addRecord', 'all') && <Button onClick={openAddModal}>
                <FaPlus className="me-1" /> Add Record
              </Button>
            )}
          </div>

             <div className="row mt-4 g-3 mx-2">
              <div className="col-md-3">
                <div className="card text-center shadow-sm">
                  <div className="card-body">
                    <small className="text-muted">Total Payment</small>
                    <h4 className="fw-bold mt-1">{totalRecords?.total_payment || 0}</h4>
                  </div>
                </div>
              </div>
               <div className="col-md-3">
                <div className="card text-center shadow-sm">
                  <div className="card-body">
                    <small className="text-muted">Total Deposit</small>
                    <h4 className="fw-bold mt-1">{totalRecords?.total_deposit || 0}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card text-center shadow-sm">
                  <div className="card-body">
                    <small className="text-muted">Total Balance</small>
                    <h4 className="fw-bold mt-1">{totalRecords?.total_payment - totalRecords?.total_deposit || 0}</h4>
                  </div>
                </div>
              </div>
          </div>

          <div className="card-body">
            <DataTable
              columns={columns}
              data={records}
              progressPending={loading}
              progressComponent={<Loading />}
              pagination
              noDataComponent="No records found"
            />
          </div>
        </div>
      </div>

      <Modal show={showModal} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editMode ? "Edit" : "Add"} Record</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="mb-2">
            <label>Date</label>
            <input
              type="date"
              className={`form-control ${errors.payment_date ? "is-invalid" : ""}`}
              value={formData.payment_date}
              onChange={(e) =>
                setFormData({ ...formData, payment_date: e.target.value })
              }
            />
            <div className="invalid-feedback">{errors.payment_date}</div>
          </div>

          <div className="mb-2">
            <label>Deposit</label>
            <input
              type="number"
              className={`form-control ${errors.deposit ? "is-invalid" : ""}`}
              value={formData.deposit}
              onChange={(e) =>
                setFormData({ ...formData, deposit: e.target.value })
              }
            />
            <div className="invalid-feedback">{errors.deposit}</div>
          </div>

          <div className="mb-2">
            <label>Payment</label>
            <input
              type="number"
              className={`form-control ${errors.payment ? "is-invalid" : ""}`}
              value={formData.payment}
              onChange={(e) =>
                setFormData({ ...formData, payment: e.target.value })
              }
            />
            <div className="invalid-feedback">{errors.payment}</div>
          </div>

          <div className="mb-2">
            <label>Detail</label>
            <textarea
              className={`form-control ${errors.detail ? "is-invalid" : ""}`}
              value={formData.detail}
              onChange={(e) =>
                setFormData({ ...formData, detail: e.target.value })
              }
            />
            <div className="invalid-feedback">{errors.detail}</div>
          </div>

          <div className="mb-2">
            <label>Recurring</label>
            <select
              className="form-control"
              value={formData.recurring_type}
              onChange={(e) =>
                setFormData({ ...formData, recurring_type: e.target.value })
              }
            >
              <option value="none">None</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            <FaSave className="me-1" /> Save
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default LeasureList;
